import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "./cn";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=closed]:hidden data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  // 이 패널은 body 로 포털되어 페이지의 글자색을 물려받지 못한다. 배경을 스스로
  // 칠하는 만큼 글자색도 같이 못박아야 배경과 짝이 어긋나지 않는다.
  "fixed z-50 gap-4 bg-background-dialog p-6 text-foreground shadow-lg transition ease-in-out data-[state=closed]:hidden data-[state=open]:animate-in data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=open]:slide-in-from-top",
        // 아래에서 올라오는 시트 — 위 두 모서리를 둥글린다.
        bottom:
          "inset-x-0 bottom-0 rounded-t-lg border-t data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=open]:slide-in-from-right",
      },
      variant: {
        default: "",
        // 왼쪽 메뉴 시트 — 데스크톱 레일과 같은 어두운 레일 색(머리·제목 글자색도 물려받는다).
        rail: "border-rail-border bg-rail text-rail-strong",
      },
    },
    defaultVariants: {
      side: "right",
      variant: "default",
    },
  }
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  isClose?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", variant, className, children, isClose = false, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, variant }), className)}
      {...props}>
      {children}
      {isClose && (
        <SheetPrimitive.Close className="tap-target absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="size-4" />
          <span className="sr-only">닫기</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const sheetHeaderVariants = cva(
  "flex w-full items-center justify-between p-4",
  {
    variants: {
      variant: {
        default: "",
        // 본문이 스크롤되는 시트의 붙는 머리 — 밑으로 지나가는 본문이 비치지 않게 배경을 칠한다.
        sticky: "sticky top-0 z-10 border-b border-border bg-background-dialog",
      },
    },
    defaultVariants: { variant: "default" },
  }
);

interface SheetHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sheetHeaderVariants> {
  isClose?: boolean;
  children: React.ReactNode;
}

const SheetHeader = ({
  className,
  variant,
  isClose = false,
  children,
  ...props
}: SheetHeaderProps) => (
  <div
    className={cn(sheetHeaderVariants({ variant }), className)}
    {...props}>
    {children}
    {isClose && (
      <SheetPrimitive.Close className="tap-target relative rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
        <X className="size-4" />
        <span className="sr-only">닫기</span>
      </SheetPrimitive.Close>
    )}
  </div>
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
